import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Layers, Rocket, Crown, Sparkles, AppWindow } from "lucide-react";

type Plan = {
  id: string;
  icon: any;
  name: string;
  tagline: string;
  quotas: { wa: string; mail: string; conn: string };
  features: string[];
  highlighted?: boolean;
  cta?: string;
};

const plans: Plan[] = [
  {
    id: "start",
    icon: Layers,
    name: "Start",
    tagline: "Estruturação inicial da campanha.",
    quotas: { wa: "20 mil", mail: "10 mil", conn: "5" },
    features: [
      "Plataforma VoxCampaign completa",
      "CRM eleitoral e segmentação territorial",
      "Treinamento da equipe incluso",
      "Suporte operacional padrão",
    ],
  },
  {
    id: "performance",
    icon: Rocket,
    name: "Performance",
    tagline: "Operação com escala e consistência.",
    quotas: { wa: "50 mil", mail: "50 mil", conn: "20" },
    features: [
      "Tudo do plano Start",
      "Automações e fluxos avançados",
      "Dashboards estratégicos completos",
      "SLA prioritário de atendimento",
    ],
    highlighted: true,
  },
  {
    id: "elite",
    icon: Crown,
    name: "Elite",
    tagline: "Estrutura enterprise para alta demanda.",
    quotas: { wa: "100 mil", mail: "100 mil", conn: "60" },
    features: [
      "Tudo do plano Performance",
      "Multi-sessão e arquitetura dedicada",
      "Configurações customizadas",
      "Atendimento executivo",
    ],
  },
  {
    id: "custom",
    icon: Sparkles,
    name: "Customizado",
    tagline: "Volume e estrutura sob medida.",
    quotas: { wa: "Sob consulta", mail: "Sob consulta", conn: "Sob consulta" },
    features: [
      "Volumes ajustados à campanha",
      "Integrações específicas",
      "Estrutura dedicada",
      "Acompanhamento estratégico",
    ],
    cta: "Falar com especialista",
  },
];

export function PlanosSection() {
  return (
    <section id="planos" className="py-24 bg-muted/30 relative overflow-hidden">
      <div className="absolute top-0 right-0 h-[500px] w-[500px] rounded-full bg-accent/10 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider">
            <AppWindow className="h-3.5 w-3.5" /> Planos do Vox App
          </div>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-foreground">
            Capacidade operacional para{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">cada estágio da campanha.</span>
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            Volumes mensais de comunicação política em escala — com a estrutura técnica da plataforma VoxCampaign.
          </p>
          <p className="mt-2 text-xs uppercase tracking-wider font-bold text-accent">
            Válido para campanha de 1 único político
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {plans.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                className={`relative rounded-3xl p-7 flex flex-col ${
                  p.highlighted
                    ? "bg-gradient-cta text-primary-foreground shadow-elegant lg:-translate-y-3 border border-white/10"
                    : "bg-card border border-border shadow-card"
                }`}
              >
                {p.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-highlight text-highlight-foreground text-[11px] font-bold uppercase tracking-wider shadow-soft">
                    Mais escolhido
                  </div>
                )}

                <div className={`h-11 w-11 rounded-2xl flex items-center justify-center mb-4 ${
                  p.highlighted ? "bg-white/15" : "bg-gradient-primary shadow-soft"
                }`}>
                  <Icon className={`h-5 w-5 ${p.highlighted ? "text-highlight" : "text-primary-foreground"}`} />
                </div>

                <div className="text-xl font-bold">{p.name}</div>
                <div className={`text-xs font-semibold mt-1 ${p.highlighted ? "text-highlight" : "text-accent"}`}>
                  {p.tagline}
                </div>

                <div className={`mt-5 grid grid-cols-3 gap-1.5 rounded-xl p-3 ${
                  p.highlighted ? "bg-white/10 border border-white/15" : "bg-muted border border-border"
                }`}>
                  {[
                    { l: "WhatsApp", v: p.quotas.wa },
                    { l: "E-mail", v: p.quotas.mail },
                    { l: "Conexões", v: p.quotas.conn },
                  ].map((q) => (
                    <div key={q.l} className="text-center">
                      <div className={`text-sm font-bold leading-tight ${p.highlighted ? "text-primary-foreground" : "text-foreground"}`}>
                        {q.v}
                      </div>
                      <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${
                        p.highlighted ? "text-primary-foreground/70" : "text-muted-foreground"
                      }`}>
                        {q.l}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`mt-1 text-[10px] uppercase tracking-wider text-center ${
                  p.highlighted ? "text-primary-foreground/60" : "text-muted-foreground"
                }`}>
                  por mês
                </div>

                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className={`mt-0.5 h-4 w-4 rounded-full flex items-center justify-center shrink-0 ${
                        p.highlighted ? "bg-highlight/25" : "bg-success/15"
                      }`}>
                        <Check className={`h-2.5 w-2.5 ${p.highlighted ? "text-highlight" : "text-success"}`} strokeWidth={3} />
                      </span>
                      <span className={p.highlighted ? "text-primary-foreground/90" : "text-foreground"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  <Button
                    asChild
                    size="lg"
                    className={`w-full h-11 text-sm ${
                      p.highlighted
                        ? "bg-highlight text-highlight-foreground hover:opacity-95 shadow-glow"
                        : "bg-foreground text-background hover:opacity-90"
                    }`}
                  >
                    <a href="#cta">
                      {p.cta ?? "Solicitar proposta"} <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
